"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  SessionProvider,
  signIn as nextAuthSignIn,
  signOut as nextAuthSignOut,
  useSession,
} from "next-auth/react";
import {
  clearGuestCookie,
  clearGuestGarden,
  isGuestCookieSet,
  setGuestCookie,
} from "@/lib/guest-garden";

export type Profile = {
  id: string;
  plan: string;
  first_name: string | null;
  zone_fr: string | null;
  ai_tokens_used: number;
  ai_tokens_reset: string;
  render3d_count: number;
  render3d_reset: string;
  weekly_email: boolean;
  created_at: string;
};

type User = {
  id: string;
  email: string | null;
  name: string | null;
  image: string | null;
};

interface AuthState {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  isGuest: boolean;
}

interface AuthContextValue extends AuthState {
  signUp: (
    email: string,
    password: string,
    token: string,
  ) => Promise<{ error: string | null }>;
  signIn: (
    email: string,
    password: string,
  ) => Promise<{ error: string | null; user: User | null }>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: string | null }>;
  refreshProfile: () => Promise<void>;
  enterGuestMode: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function AuthStateProvider({ children }: { children: ReactNode }) {
  const { data: session, status, update: updateSession } = useSession();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [hasGuestCookie, setHasGuestCookie] = useState(false);
  const lastFetchedUserId = useRef<string | null>(null);

  const user: User | null = session?.user
    ? {
        id: (session.user as { id?: string }).id ?? "",
        email: session.user.email ?? null,
        name: session.user.name ?? null,
        image: session.user.image ?? null,
      }
    : null;

  useEffect(() => {
    setHasGuestCookie(isGuestCookieSet());
  }, []);

  // A real session always wins: someone who signs in while a guest cookie is
  // still around is not a guest, and must not see the guest banner.
  const isGuest = !user && hasGuestCookie;

  const fetchProfile = useCallback(async (): Promise<Profile | null> => {
    try {
      const res = await fetch("/api/profile", { cache: "no-store" });
      if (!res.ok) return null;
      return (await res.json()) as Profile;
    } catch (e) {
      console.warn("[auth] fetchProfile exception:", e);
      return null;
    }
  }, []);

  useEffect(() => {
    if (!user?.id) {
      setProfile(null);
      lastFetchedUserId.current = null;
      return;
    }
    if (lastFetchedUserId.current === user.id) return;
    lastFetchedUserId.current = user.id;
    fetchProfile().then((p) => setProfile(p));
  }, [user?.id, fetchProfile]);

  const refreshProfile = useCallback(async () => {
    const p = await fetchProfile();
    if (p) setProfile(p);
  }, [fetchProfile]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const res = await nextAuthSignIn("credentials", {
        email,
        password,
        redirect: false,
      });
      if (!res) {
        return { error: "Erreur réseau", user: null };
      }
      if (res.error) {
        return { error: "Identifiants invalides", user: null };
      }
      // Signing in ends guest mode: drop the cookie so the middleware stops
      // treating this browser as a guest.
      clearGuestCookie();
      setHasGuestCookie(false);
      // Trigger a session refresh so useSession reflects the new user immediately.
      await updateSession();
      return { error: null, user: null };
    },
    [updateSession],
  );

  const signUp = useCallback(
    async (email: string, password: string, token: string) => {
      const res = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, token }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        return { error: body.error ?? "Inscription impossible" };
      }
      // Auto-login after successful signup so the user lands authenticated.
      const loginRes = await nextAuthSignIn("credentials", {
        email,
        password,
        redirect: false,
      });
      if (loginRes?.error) {
        return { error: "Compte créé, mais connexion échouée — réessayez." };
      }
      clearGuestCookie();
      setHasGuestCookie(false);
      await updateSession();
      return { error: null };
    },
    [updateSession],
  );

  const signInWithGoogle = useCallback(async () => {
    clearGuestCookie();
    setHasGuestCookie(false);
    await nextAuthSignIn("google", { callbackUrl: "/dashboard" });
  }, []);

  const signOut = useCallback(async () => {
    clearGuestCookie();
    clearGuestGarden();
    setHasGuestCookie(false);
    setProfile(null);
    await nextAuthSignOut({ callbackUrl: "/" });
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const res = await fetch("/api/auth/reset-password", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    if (!res.ok) {
      return { error: "Erreur lors de la demande" };
    }
    return { error: null };
  }, []);

  const enterGuestMode = useCallback(() => {
    setGuestCookie();
    setHasGuestCookie(true);
  }, []);

  const value: AuthContextValue = {
    user,
    profile,
    loading: status === "loading",
    isGuest,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
    refreshProfile,
    enterGuestMode,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  return (
    <SessionProvider>
      <AuthStateProvider>{children}</AuthStateProvider>
    </SessionProvider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
